// ChannelList — left pane. Each channel is a TMCHub project: project icon (or a
// coloured letter avatar) + name + client, plus — when the data is available —
// a last-message preview, a timestamp, and an unread/mention badge.
//
// Drop-in replacement for ClientApp/src/components/ChannelList.jsx. Same props
// and same data conventions as before (c.id, c.name, c.clientName, c.iconUrl);
// the preview / time / unread features are ADDITIVE and degrade gracefully —
// if the channel DTO doesn't carry those fields yet, the row simply renders the
// name + client exactly as it does today. See HANDOVER.md for the DTO fields to
// add server-side to light them up.

import { useRef, useState } from "react";
import { initials } from "./Avatar.jsx";
import Icon from "./Icon.jsx";

const VARIANTS = ["a", "b", "c", "d", "e"];
const variantFor = (id) => VARIANTS[Math.abs(Number(id) || 0) % VARIANTS.length];

// WhatsApp-style relative label for the last-message time. Accepts an ISO
// string (preferred) or returns any pre-formatted string untouched.
function timeLabel(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return String(value); // already a label like "Yest"
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yest";
  if (now - d < 7 * 864e5) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
}

// Last-activity timestamp (ms) for recency sorting; 0 when the channel has no messages.
function activityAt(c) {
  const t = Date.parse(c.lastMessageAt ?? c.time ?? "");
  return Number.isNaN(t) ? 0 : t;
}

// Fade dormant channels "into the abyss" by how long since their last message. Active/unread
// channels are exempt (never fade — you must not miss them). Returned as a CSS opacity.
function fadeFor(c, unread, active) {
  if (active || unread > 0) return 1;
  const at = activityAt(c);
  if (!at) return 0.42;                 // never any activity
  const age = Date.now() - at;
  const DAY = 864e5;
  if (age < DAY) return 1;
  if (age < 7 * DAY) return 0.82;
  if (age < 30 * DAY) return 0.6;
  return 0.42;
}

const hideStatusPref = (id) => { try { return localStorage.getItem(`tmc.hideStatus.${id}`) === "1"; } catch { return false; } };

export default function ChannelList({ channels, activeId, onSelect, onMute, onToggleHideStatus, onSummarizeUnread, isStaff = false,
  pickMode = false, pickedIds, onPick, newChannelId = null }) {
  // Most-recently-active channels rise to the top; dormant ones sink (and fade, below).
  // A just-created channel (newChannelId) is pinned to the very top so it's seen sliding in,
  // even though it has no messages yet (which would otherwise sort it last).
  // Special channels (Dream Team, Reporting — any with a kind) are pinned separately below the
  // scroller (DreamTeamPin / ReportingPin) — exclude them here.
  const ordered = [...channels]
    .filter((c) => !c.kind || c.kind === "custom") // specials (Dream Team, Reporting) are pinned separately; custom channels are ordinary rows
    .sort((a, b) => {
      if (a.id === newChannelId) return -1;
      if (b.id === newChannelId) return 1;
      return activityAt(b) - activityAt(a) || (a.name || "").localeCompare(b.name || "");
    });

  // Right-click (desktop) / long-press (touch) → a little Mute/Unmute menu.
  const [menu, setMenu] = useState(null); // { channel, x, y }
  const pressTimer = useRef(null);
  const suppressClick = useRef(false);
  const openMenu = (channel, x, y) => setMenu({ channel, x: Math.min(x, window.innerWidth - 170), y });
  const closeMenu = () => setMenu(null);
  const startPress = (e, c) => {
    if (e.pointerType === "mouse") return; // desktop uses onContextMenu
    suppressClick.current = false;
    pressTimer.current = setTimeout(() => { suppressClick.current = true; openMenu(c, e.clientX, e.clientY); }, 500);
  };
  const cancelPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const clickRow = (c) => { if (suppressClick.current) { suppressClick.current = false; return; } onSelect(c); };

  return (
    <nav className="channels" data-tour="channels">
      {channels.length === 0 && (
        <div className="channels__empty">
          <span className="emoji-lg">💬</span>
          No channels yet.
        </div>
      )}
      {ordered.map((c, i) => {
        // tolerate either the new DTO names or the older ones
        const preview = c.lastMessagePreview ?? c.lastMessage ?? "";
        const time = timeLabel(c.lastMessageAt ?? c.time ?? "");
        const unread = c.unreadCount ?? c.unread ?? 0;
        const mentioned = c.mentioned ?? c.mention ?? false;
        const muted = c.isMuted ?? c.muted ?? false;
        const fade = fadeFor(c, unread, c.id === activeId);

        const picked = pickMode && pickedIds?.has(c.id);
        const isNew = c.id === newChannelId;
        const cls = [
          "channel",
          !pickMode && c.id === activeId && "channel--active",
          unread > 0 && "channel--unread",
          mentioned && "channel--ping",
          picked && "channel--picked",
          isNew && "channel--new",
        ].filter(Boolean).join(" ");

        return (
          <button key={c.id} className={cls} data-tour={i === 0 ? "channelrow" : undefined} style={{ "--chan-op": pickMode || isNew ? 1 : fade }}
                  onClick={() => (pickMode ? onPick?.(c) : clickRow(c))}
                  onContextMenu={(e) => { e.preventDefault(); if (!pickMode) openMenu(c, e.clientX, e.clientY); }}
                  onPointerDown={(e) => { if (!pickMode) startPress(e, c); }}
                  onPointerUp={cancelPress} onPointerLeave={cancelPress} onPointerCancel={cancelPress}>
            {pickMode && <span className={`channel__pick ${picked ? "channel__pick--on" : ""}`} aria-hidden>{picked ? "✓" : ""}</span>}
            <span className={`avatar avatar--${variantFor(c.id)}`} aria-hidden>
              {c.iconUrl ? <img src={c.iconUrl} alt="" /> : (c.name ? initials(c.name) : "#")}
            </span>

            <span className="channel__meta">
              <span className="channel__row">
                <span className={`channel__name channel__name--${variantFor(c.id)}`}>{c.name}</span>
              </span>
              {c.clientName && <span className="channel__client">{c.clientName}</span>}
              {preview && <span className="channel__preview">{preview}</span>}
            </span>

            {(time || unread > 0 || muted) && (
              <span className="channel__end">
                {time && <span className="channel__time">{time}</span>}
                {unread > 0 && (
                  <span className={`unread ${mentioned ? "unread--ping" : ""}`}>{unread}</span>
                )}
                {muted && <span className="channel__mute">🔕</span>}
              </span>
            )}
          </button>
        );
      })}

      {menu && (() => {
        const isMuted = menu.channel.isMuted ?? menu.channel.muted ?? false;
        const statusHidden = hideStatusPref(menu.channel.id);
        const unread = menu.channel.unreadCount ?? menu.channel.unread ?? 0;
        return (
          <>
            <div className="chan-menu__overlay" onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} />
            <div className="chan-menu" style={{ left: menu.x, top: menu.y }}>
              {isStaff && unread > 10 && (
                <button className="chan-menu__item" onClick={() => { onSummarizeUnread?.(menu.channel); closeMenu(); }}>
                  <Icon name="refresh" className="ic--sm" />
                  Summarize {unread} unread
                </button>
              )}
              <button className="chan-menu__item" onClick={() => { onMute?.(menu.channel, !isMuted); closeMenu(); }}>
                <Icon name={isMuted ? "volume" : "volume-x"} className="ic--sm" />
                {isMuted ? "Unmute channel" : "Mute channel"}
              </button>
              <button className="chan-menu__item" onClick={() => { onToggleHideStatus?.(menu.channel, !statusHidden); closeMenu(); }}>
                <Icon name="refresh" className="ic--sm" />
                {statusHidden ? "Show status updates" : "Hide status updates"}
              </button>
            </div>
          </>
        );
      })()}
    </nav>
  );
}
